// src/pages/PrivacyPolicy.jsx
import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

const PrivacyPolicy = () => {
  return (
    <>
      <SEO 
        title="Privacy Policy | Providence Federal Group"
        description="Read our privacy policy to understand how we collect, use, and protect your personal information."
        keywords="privacy policy, data protection, GDPR, CCPA, privacy"
      />
    <div className="min-h-screen flex flex-col">
      {/* Header modifié pour la page Privacy Policy */}
      <Header />
      
      {/* Contenu principal */}
      <main className="flex-grow py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          
          {/* En-tête de la page */}
          <div className="text-center mb-16">
            <h1 className="text-5xl font-light text-gray-900 mb-6 mt-20">
              Privacy Policy
            </h1>
            <div className="w-24 h-1 bg-[#C5A047] mx-auto mb-8"></div>
            <p className="text-xl text-gray-600">
              Providence Federal Group LLC - Effective Date: January 1, 2026
            </p>
          </div>

          {/* Introduction */}
          <div className="mb-12">
            <h2 className="text-2xl font-semibold text-[#2C3E50] mb-6">
              Our Commitment to Your Privacy
            </h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Providence Federal Group LLC ("PFG", "we", "us", or "our") is committed to protecting 
              the privacy and security of your personal information. This Privacy Policy describes how 
              we collect, use, disclose, and safeguard your information when you visit our website 
              https://providencefederalgroup.com (the "Site") or engage with our federal contracting services.
            </p>
            <p className="text-gray-700 leading-relaxed">
              As a Service-Disabled Veteran-Owned Small Business operating in the government contracting 
              sector, we adhere to strict privacy standards in compliance with applicable federal regulations 
              including the Privacy Act of 1974 and Federal Acquisition Regulation (FAR) requirements.
            </p>
          </div>

          {/* Section 1 */}
          <div className="mb-12">
            <h2 className="text-2xl font-semibold text-[#2C3E50] mb-6">
              1. Information We Collect
            </h2>
            
            <h3 className="text-xl font-medium text-gray-800 mb-4">
              1.1 Information You Provide
            </h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-3 mb-6">
              <li>
                <span className="font-medium">Contact Information:</span> Name, email address, phone number, 
                company name, and job title when you submit inquiries or request proposals
              </li>
              <li>
                <span className="font-medium">Business Information:</span> Company details, DUNS number, 
                CAGE code, NAICS codes, and federal contracting certifications
              </li>
              <li>
                <span className="font-medium">Communication Data:</span> Correspondence through email, 
                contact forms, or other communication channels
              </li>
              <li>
                <span className="font-medium">Proposal Information:</span> Documents and information 
                submitted for federal contracting opportunities
              </li>
            </ul>

            <h3 className="text-xl font-medium text-gray-800 mb-4">
              1.2 Automatically Collected Information
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              When you visit our Site, we may automatically collect certain information about your device, 
              including:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-3">
              <li>IP address, browser type, and operating system</li>
              <li>Pages visited, time spent on pages, and navigation patterns</li>
              <li>Referring website or search terms</li>
              <li>Date and time of access</li>
            </ul>
          </div>

          {/* Section 2 */}
          <div className="mb-12">
            <h2 className="text-2xl font-semibold text-[#2C3E50] mb-6">
              2. How We Use Your Information
            </h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              We use the information we collect for legitimate business purposes, including:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-3">Federal Contracting Operations</h4>
                <ul className="list-disc pl-5 text-gray-700 text-sm space-y-2">
                  <li>Responding to RFPs and government solicitations</li>
                  <li>Preparing and submitting proposals</li>
                  <li>Managing contractor-client relationships</li>
                  <li>Compliance with federal reporting requirements</li>
                </ul>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-3">Business Communications</h4>
                <ul className="list-disc pl-5 text-gray-700 text-sm space-y-2">
                  <li>Responding to inquiries and information requests</li>
                  <li>Sending updates about our services and capabilities</li>
                  <li>Providing requested documentation and certifications</li>
                  <li>Facilitating partnerships and teaming arrangements</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Section 3 */}
          <div className="mb-12">
            <h2 className="text-2xl font-semibold text-[#2C3E50] mb-6">
              3. Information Sharing and Disclosure
            </h2>
            
            <div className="space-y-6">
              <div className="border-l-4 border-[#C5A047] pl-6">
                <h3 className="text-xl font-medium text-gray-800 mb-3">
                  Federal Government Entities
                </h3>
                <p className="text-gray-700">
                  We may disclose information to federal agencies as required for contract performance, 
                  compliance with the Federal Acquisition Regulation (FAR), or in response to lawful 
                  requests from government authorities.
                </p>
              </div>
              
              <div className="border-l-4 border-blue-500 pl-6">
                <h3 className="text-xl font-medium text-gray-800 mb-3">
                  Service Providers
                </h3>
                <p className="text-gray-700">
                  We engage trusted third-party service providers who assist in our operations, such as 
                  IT support, legal counsel, and accounting services. These providers are contractually 
                  obligated to protect your information.
                </p>
              </div>
              
              <div className="border-l-4 border-emerald-500 pl-6">
                <h3 className="text-xl font-medium text-gray-800 mb-3">
                  Legal Requirements
                </h3>
                <p className="text-gray-700">
                  We may disclose information if required by law, court order, or government regulation, 
                  or to protect the rights, property, or safety of PFG, our clients, or others.
                </p>
              </div>
            </div>
          </div>

          {/* Section 4 */}
          <div className="mb-12">
            <h2 className="text-2xl font-semibold text-[#2C3E50] mb-6">
              4. Data Security
            </h2>
            <div className="bg-gray-50 p-8 rounded-xl">
              <p className="text-gray-700 leading-relaxed mb-6">
                We implement appropriate technical and organizational security measures to protect your 
                personal information against unauthorized access, alteration, disclosure, or destruction. 
                Our security practices include:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-[#2C3E50] text-white rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="font-bold">E</span>
                  </div>
                  <h4 className="font-semibold text-gray-800 mb-2">Encryption</h4>
                  <p className="text-gray-600 text-sm">Data encryption for sensitive information</p>
                </div>
                
                <div className="text-center">
                  <div className="w-12 h-12 bg-[#C5A047] text-white rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="font-bold">A</span>
                  </div>
                  <h4 className="font-semibold text-gray-800 mb-2">Access Controls</h4>
                  <p className="text-gray-600 text-sm">Role-based access and authentication</p>
                </div>
                
                <div className="text-center">
                  <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="font-bold">C</span>
                  </div>
                  <h4 className="font-semibold text-gray-800 mb-2">Compliance</h4>
                  <p className="text-gray-600 text-sm">NIST SP 800-171 and CMMC aligned</p>
                </div>
              </div>
            </div>
          </div>

          {/* Section 5 */}
          <div className="mb-12">
            <h2 className="text-2xl font-semibold text-[#2C3E50] mb-6">
              5. Your Rights and Choices
            </h2>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Right
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                      Access
                    </td>
                    <td className="px-6 py-4 text-gray-700">
                      Request access to your personal information we hold
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                      Correction
                    </td>
                    <td className="px-6 py-4 text-gray-700">
                      Request correction of inaccurate or incomplete information
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                      Deletion
                    </td>
                    <td className="px-6 py-4 text-gray-700">
                      Request deletion of your personal information, subject to legal obligations
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                      Opt-Out
                    </td>
                    <td className="px-6 py-4 text-gray-700">
                      Opt-out of marketing communications at any time
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-gradient-to-r from-[#2C3E50] to-[#1E293B] rounded-2xl p-8 text-white">
            <h2 className="text-2xl font-light mb-6">Contact Us</h2>
            <p className="mb-6">
              If you have questions about this Privacy Policy or our privacy practices, please contact:
            </p>
            
            <div className="space-y-4">
              <div className="flex items-start">
                <svg className="w-5 h-5 mt-1 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <div>
                  <div className="font-medium">Privacy Officer</div>
                  <a href="mailto:support@providencefederal.com" className="text-[#C5A047] hover:underline">
                  support@providencefederal.com
                  </a>
                </div>
              </div>
              
              <div className="flex items-start">
                <svg className="w-5 h-5 mt-1 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <div>
                  <div className="font-medium">Mailing Address</div>
                  <span className="font-medium">Providence's Financial District</span><br />
                  <span>📍 10 Dorrance Street Suite 700</span><br />
                  <span>Providence, RI 02903</span><br />
                  <span>United States</span>
                </div>
              </div>
            </div>
          </div>

          {/* Effective Date */}
          <div className="mt-12 pt-8 border-t border-gray-200 text-center">
            <p className="text-gray-600">
              <span className="font-medium">Effective Date:</span> November 1, 2025
            </p>
            <p className="text-gray-500 text-sm mt-2">
              This Privacy Policy may be updated periodically. Please check this page for the most current version.
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
    </>
  );
};

export default PrivacyPolicy;